import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Plus, ArrowUpRight, X, Calculator } from "lucide-react";
import { toast } from "sonner";
import OSLayout from "../../components/os/OSLayout";
import ShareDialog from "../../components/os/ShareDialog";
import ItemMenu from "../../components/os/ItemMenu";
import OwnerFilter from "../../components/os/OwnerFilter";
import { useTeamFilter } from "../../hooks/useTeamFilter";
import { api, formatApiError } from "../../lib/api";
import { useI18n } from "../../context/I18nContext";
import StudioFinancePipeline from "../../components/os/StudioFinancePipeline";
import TrashDialog, { TrashButton } from "../../components/os/TrashDialog";

const ease = [0.16, 1, 0.3, 1];
const STATUS_CLS = {
  activo: "bg-violet/10 text-violet",
  en_revision: "bg-gold/15 text-gold",
  completado: "bg-emerald-500/10 text-emerald-600",
  pausado: "bg-navy/10 text-navy/50",
};

export default function GoutStudio() {
  const { t, lang } = useI18n();
  const tf = useTeamFilter();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", client: "", sector: "Hospitality", budget: 0 });
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [share, setShare] = useState(null);
  const [trashOpen, setTrashOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const [editForm, setEditForm] = useState({ name: "", client: "", sector: "Hospitality", budget: 0 });

  const load = () => api.get("/gout/overview").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const openEdit = (p) => {
    setEdit(p);
    setEditForm({ name: p.name, client: p.client, sector: p.sector, budget: p.budget || 0 });
  };
  const saveEdit = async (e) => {
    e.preventDefault();
    try {
      await api.patch(`/gout/projects/${edit.id}`, { ...editForm, budget: Number(editForm.budget) || 0 });
      setEdit(null);
      toast.success("Proyecto actualizado");
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Error"); }
  };
  const doDelete = async (p) => {
    if (!window.confirm(`¿Eliminar el proyecto "${p.name}"? Se moverá a la papelera y podrás restaurarlo durante 30 días.`)) return;
    try {
      await api.delete(`/gout/projects/${p.id}`);
      toast.success("Movido a la papelera (30 días)");
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Error"); }
  };

  const create = async (e) => {
    e.preventDefault();
    setError(""); setSaving(true);
    try {
      const { data: p } = await api.post("/gout/projects", { ...form, budget: Number(form.budget) || 0 });
      setOpen(false);
      navigate(`/os/gout/${p.id}`);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setSaving(false); }
  };

  const fmtBudget = (p) =>
    (p.value ?? 0) > 0 ? `$${Number(p.value).toLocaleString("es-MX", { maximumFractionDigits: 0 })}`
      : p.budget ? `$${Number(p.budget).toLocaleString()}` : "—";

  return (
    <OSLayout title={t("os.titles.gout")}>
      {!data ? (
        <div className="text-navy/40">{t("os.loading")}</div>
      ) : (
        <div className="space-y-8">
          <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
            <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, ease }}>
              <div className="overline text-navy/40 mb-2">GOÛT · Design Intelligence Studio</div>
              <h2 className="font-editorial text-4xl md:text-5xl tracking-tight">Studio</h2>
              <p className="mt-2 text-navy/50">{t("os.gout.subtitle")}</p>
            </motion.div>
            <div className="flex items-center gap-3">
              <OwnerFilter tf={tf} />
              <TrashButton testid="gout-trash-btn" onClick={() => setTrashOpen(true)} />
              <Link to="/os/gout-calculadora" data-testid="gout-calc-link"
                className="inline-flex items-center gap-2 rounded-full border border-navy/15 text-navy/70 text-sm px-5 py-3 hover:border-navy/40 transition-colors">
                <Calculator className="w-4 h-4" /> Calculadora de Interiores
              </Link>
              <button data-testid="gout-new-project" onClick={() => setOpen(true)}
                className="inline-flex items-center gap-2 rounded-full bg-gradient-violet text-white text-sm px-5 py-3 hover:opacity-90 transition-opacity">
                <Plus className="w-4 h-4" /> {t("os.gout.newProject")}
              </button>
            </div>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {data.metrics.map((m, i) => (
              <motion.div key={m.key} initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, ease, delay: i * 0.06 }} data-testid={`gout-kpi-${m.key}`}
                className="bg-white border border-navy/10 rounded-lg p-6">
                <div className="overline text-navy/40">{lang === "en" ? m.label_en : m.label_es}</div>
                <div className="mt-3 text-3xl font-medium tracking-tight">{m.value}</div>
              </motion.div>
            ))}
          </div>

          {data.projects.length === 0 ? (
            <div className="text-navy/40 py-16 text-center border border-dashed border-navy/15 rounded-lg">{t("os.gout.empty")}</div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {tf.filter(data.projects).map((p, i) => (
                <motion.div key={p.id} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, ease, delay: (i % 3) * 0.06 }} className="relative">
                  <div className="absolute top-4 right-4 z-10">
                    <ItemMenu testId={p.id}
                      onInvite={() => setShare(p)}
                      onEdit={() => openEdit(p)}
                      onDelete={() => doDelete(p)} />
                  </div>
                  <Link to={`/os/gout/${p.id}`} data-testid={`gout-project-${p.id}`}
                    className="group block h-full bg-white border border-navy/10 rounded-lg p-6 hover:-translate-y-1 hover:border-navy/25 transition-all duration-500">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <h3 className="text-xl font-medium tracking-tight">{p.name}</h3>
                        <div className="text-sm text-navy/50 mt-1">{p.client} · {p.sector}</div>
                      </div>
                      <ArrowUpRight className="w-5 h-5 text-navy/30 group-hover:text-violet transition-colors shrink-0 mr-8" />
                    </div>
                    <div className="mt-5 flex items-center justify-between">
                      <span className={`text-[10px] px-2 py-1 rounded-full ${STATUS_CLS[p.status]}`}>{t(`os.gout.status.${p.status}`)}</span>
                      <span className="text-sm text-navy/50" title="Valor por cotizaciones aprobadas">{fmtBudget(p)}</span>
                    </div>
                    <div className="mt-4">
                      <div className="flex justify-between text-xs text-navy/40 mb-1.5">
                        <span>{t("os.gout.progress")}</span><span>{p.progress}%</span>
                      </div>
                      <div className="h-1.5 rounded-full bg-navy/5 overflow-hidden">
                        <div className="h-full bg-gradient-violet" style={{ width: `${p.progress}%` }} />
                      </div>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      )}

      <div className="mt-8"><StudioFinancePipeline studio="gout" /></div>

      {/* Create modal */}
      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-navy/40 backdrop-blur-sm" onClick={() => setOpen(false)}>
          <motion.div initial={{ opacity: 0, y: 20, scale: 0.98 }} animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 0.4, ease }} onClick={(e) => e.stopPropagation()}
            data-testid="gout-create-modal" className="w-full max-w-md bg-cream rounded-xl p-8">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-medium">{t("os.gout.create.title")}</h3>
              <button onClick={() => setOpen(false)} className="text-navy/40 hover:text-navy"><X className="w-5 h-5" /></button>
            </div>
            <form onSubmit={create} className="space-y-4">
              <input required data-testid="gout-input-name" placeholder={t("os.gout.create.name")} value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="w-full bg-white border border-navy/10 rounded-lg px-4 py-3 outline-none focus:border-violet transition-colors" />
              <input required data-testid="gout-input-client" placeholder={t("os.gout.create.client")} value={form.client}
                onChange={(e) => setForm({ ...form, client: e.target.value })}
                className="w-full bg-white border border-navy/10 rounded-lg px-4 py-3 outline-none focus:border-violet transition-colors" />
              <select data-testid="gout-input-sector" value={form.sector} onChange={(e) => setForm({ ...form, sector: e.target.value })}
                className="w-full bg-white border border-navy/10 rounded-lg px-4 py-3 outline-none focus:border-violet transition-colors">
                <option>Hospitality</option><option>Corporate</option><option>Retail</option><option>Residential</option><option>Healthcare</option>
              </select>
              <input type="number" data-testid="gout-input-budget" placeholder={t("os.gout.create.budget")} value={form.budget || ""}
                onChange={(e) => setForm({ ...form, budget: e.target.value })}
                className="w-full bg-white border border-navy/10 rounded-lg px-4 py-3 outline-none focus:border-violet transition-colors" />
              {error && <p className="text-sm text-destructive">{error}</p>}
              <button data-testid="gout-create-submit" disabled={saving}
                className="w-full rounded-full bg-gradient-violet text-white py-3.5 hover:opacity-90 transition-opacity disabled:opacity-60">
                {t("os.gout.create.submit")}
              </button>
            </form>
          </motion.div>
        </div>
      )}
      {/* Edit modal */}
      {edit && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-navy/40 backdrop-blur-sm" onClick={() => setEdit(null)}>
          <motion.div initial={{ opacity: 0, y: 20, scale: 0.98 }} animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 0.4, ease }} onClick={(e) => e.stopPropagation()}
            data-testid="gout-edit-modal" className="w-full max-w-md bg-cream rounded-xl p-8">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-medium">Editar proyecto</h3>
              <button onClick={() => setEdit(null)} className="text-navy/40 hover:text-navy"><X className="w-5 h-5" /></button>
            </div>
            <form onSubmit={saveEdit} className="space-y-4">
              <input required data-testid="gout-edit-name" placeholder={t("os.gout.create.name")} value={editForm.name}
                onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                className="w-full bg-white border border-navy/10 rounded-lg px-4 py-3 outline-none focus:border-violet transition-colors" />
              <input required data-testid="gout-edit-client" placeholder={t("os.gout.create.client")} value={editForm.client}
                onChange={(e) => setEditForm({ ...editForm, client: e.target.value })}
                className="w-full bg-white border border-navy/10 rounded-lg px-4 py-3 outline-none focus:border-violet transition-colors" />
              <select data-testid="gout-edit-sector" value={editForm.sector} onChange={(e) => setEditForm({ ...editForm, sector: e.target.value })}
                className="w-full bg-white border border-navy/10 rounded-lg px-4 py-3 outline-none focus:border-violet transition-colors">
                <option>Hospitality</option><option>Corporate</option><option>Retail</option><option>Residential</option><option>Healthcare</option>
              </select>
              <input type="number" data-testid="gout-edit-budget" placeholder={t("os.gout.create.budget")} value={editForm.budget || ""}
                onChange={(e) => setEditForm({ ...editForm, budget: e.target.value })}
                className="w-full bg-white border border-navy/10 rounded-lg px-4 py-3 outline-none focus:border-violet transition-colors" />
              <button data-testid="gout-edit-submit"
                className="w-full rounded-full bg-gradient-violet text-white py-3.5 hover:opacity-90 transition-opacity">Guardar cambios</button>
            </form>
          </motion.div>
        </div>
      )}

      {share && (
        <ShareDialog collection="gout_projects" id={share.id} title={share.name} onClose={() => setShare(null)} />
      )}
      {trashOpen && <TrashDialog onClose={() => setTrashOpen(false)} onChanged={load} testid="gout-trash-dialog" />}
    </OSLayout>
  );
}
